import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { PostDatesRequest } from './interfaces/post-dates.request';
import { PostDatesResponse } from './interfaces/post-dates.response';
import { GetDatesSummaryResponse } from './interfaces/get-summary.response';
import { PostRsvpRequest } from './interfaces/post-rsvp.request';
import { getTripId } from '@at/trip';
import { PostAvailabilityRequest } from './interfaces/post-availability.request';
import { PostRangeRequest } from './interfaces/post-range.request';
import { addTimezoneOffset, removeTimezoneOffset } from '@at/shared';

/* const MOCK_DATA = {
  GATHER_AVAILABILITY_MOCK: {
    dateSummary: {
      state: 'GatheringAvailability',
      availability: {
        membersAvailability: [
          {
            userId: '123',
            availability: ['2024-07-22', '2024-07-23', '2024-07-24', '2024-07-25'].map(
              (v) => new Date(v),
            ),
          },
          {
            userId: '456',
            availability: ['2024-07-23', '2024-07-24', '2024-07-25', '2024-07-26'].map(
              (v) => new Date(v),
            ),
          },
          {
            userId: '789',
            availability: ['2024-07-24', '2024-07-25', '2024-07-26', '2024-07-27'].map(
              (v) => new Date(v),
            ),
          },
        ],
        combinedAvailability: {
          [new Date('2024-08-22').toDateString()]: 1,
          [new Date('2024-08-23').toDateString()]: 2,
          [new Date('2024-08-24').toDateString()]: 3,
          [new Date('2024-08-25').toDateString()]: 3,
          [new Date('2024-08-26').toDateString()]: 2,
          [new Date('2024-08-27').toDateString()]: 0,
        },
      },
      rangeCriteria: {
        start: new Date(2024, 6, 22),
        end: new Date(2024, 6, 27),
        minDuration: 4,
      },
      locked: false,
    },
  } as GetDatesSummaryResponse,

  GATHER_RSVP_MOCK: {
    dateSummary: {
      state: 'GatheringRSVP',
      selectedDates: {
        start: new Date(2024, 7, 21),
        end: new Date(2024, 7, 28),
      },
      locked: false,
    },
  } as GetDatesSummaryResponse,

  NOT_STARTED_MOCK: {
    dateSummary: {
      state: 'NotStarted',
      locked: false,
    },
  } as GetDatesSummaryResponse,

  ERROR_MOCK: {
    errorMessage: 'Could not fetch dates, network error.',
  } as ErrorResponse,
}; */

@Injectable()
export class DatesApi {
  #http = inject(HttpClient);
  #tripId = getTripId();

  public getSummary(tripId = this.#tripId): Observable<GetDatesSummaryResponse> {
    return this.#http.get<GetDatesSummaryResponse>(`trip/${tripId}/dates/summary`).pipe(
      map((response) => {
        if (response.dateSummary.selectedDates) {
          response.dateSummary.selectedDates = {
            start: addTimezoneOffset(response.dateSummary.selectedDates.start),
            end: addTimezoneOffset(response.dateSummary.selectedDates.end),
          };
        }
        if (response.dateSummary.rangeCriteria) {
          response.dateSummary.rangeCriteria = {
            ...response.dateSummary.rangeCriteria,
            start: addTimezoneOffset(response.dateSummary.rangeCriteria.start),
            end: addTimezoneOffset(response.dateSummary.rangeCriteria.end),
          };
        }

        if (response.dateSummary.availabilityResponse?.availability) {
          response.dateSummary.availabilityResponse.availability =
            response.dateSummary.availabilityResponse.availability.map((date) =>
              addTimezoneOffset(date),
            );
        }

        if (response.dateSummary.groupResponses?.availability) {
          const combinedAvailability =
            response.dateSummary.groupResponses.availability.combinedAvailability;

          response.dateSummary.groupResponses.availability.combinedAvailability =
            Object.fromEntries(
              Object.entries(combinedAvailability).map(([key, value]) => [
                addTimezoneOffset(new Date(key)).toISOString(),
                value,
              ]),
            );

          const membersAvailability =
            response.dateSummary.groupResponses.availability.membersAvailability;

          response.dateSummary.groupResponses.availability.membersAvailability =
            membersAvailability.map((member) => ({
              ...member,
              availability: member.availability.map((date) => addTimezoneOffset(date)),
            }));
        }

        return { ...response };
      }),
    );
  }

  public postDates(dates: PostDatesRequest, tripId = this.#tripId): Observable<PostDatesResponse> {
    dates.start = removeTimezoneOffset(dates.start);
    dates.end = removeTimezoneOffset(dates.end);
    return this.#http.post<PostDatesResponse>(`trip/${tripId}/dates`, dates);
  }

  public postRange(range: PostRangeRequest, tripId = this.#tripId): Observable<PostDatesResponse> {
    range.start = removeTimezoneOffset(range.start);
    range.end = removeTimezoneOffset(range.end);
    return this.#http.post<PostDatesResponse>(`trip/${tripId}/dates/range`, range);
  }

  public postRsvp(rsvp: PostRsvpRequest, tripId = this.#tripId) {
    return this.#http.post(`trip/${tripId}/dates/rsvp`, rsvp);
  }

  public postAvailability(availability: Date[], tripId = this.#tripId) {
    const availabilityRequest: PostAvailabilityRequest = {
      availability: availability.map((date) => removeTimezoneOffset(date)),
    };
    return this.#http.post(`trip/${tripId}/dates/availability`, availabilityRequest);
  }

  public postLock(tripId = this.#tripId) {
    return this.#http.post(`trip/${tripId}/dates/lock`, {});
  }

  public postUnlock(tripId = this.#tripId) {
    return this.#http.post(`trip/${tripId}/dates/unlock`, {});
  }
}
